import { HttpApiSchema } from "@effect/platform";
import { Schema } from "effect";
import {
  NewUiComponentResult,
  UiComponentCustomerEdits,
  UiComponentDecision,
  UiComponentStatus,
} from "../models/shopper-ai/conversation/message/ui-component";
import { WidgetConversationResponseEffectSchema } from "../models/shopper-ai/conversation/widget-conversation";

/** Approves or denies an action UI component message; a sign-in one is approved by {@link SignInUiComponentResult}. */
export const DecideUiComponentResult = Schema.TaggedStruct("decide", {
  requestId: Schema.String,
  decision: UiComponentDecision,
  customerEdits: Schema.optional(UiComponentCustomerEdits),
});
export type DecideUiComponentResult = typeof DecideUiComponentResult.Type;

/** Records an add-to-cart on a product carousel; the cart itself changes in the browser. */
export const AddToCartUiComponentResult = Schema.TaggedStruct("addToCart", {
  requestId: Schema.String,
  productId: Schema.String,
  variantId: Schema.String,
  quantity: Schema.Number.pipe(Schema.int(), Schema.positive()),
});
export type AddToCartUiComponentResult = typeof AddToCartUiComponentResult.Type;

/** Completes a sign-in UI component message. The recorded email comes from the verified token, never the client. */
export const SignInUiComponentResult = Schema.TaggedStruct("signIn", {
  requestId: Schema.String,
  customerAuthToken: Schema.String,
});
export type SignInUiComponentResult = typeof SignInUiComponentResult.Type;

export const UiComponentResultInput = Schema.Union(
  DecideUiComponentResult,
  AddToCartUiComponentResult,
  SignInUiComponentResult,
);
export type UiComponentResultInput = typeof UiComponentResultInput.Type;

/** The UI component result as recorded, plus the refreshed conversation; the agent's reply also streams over SSE. */
export const UiComponentResultResponse = Schema.Struct({
  /** Null when the action was still running when the request stopped waiting; the stream delivers it. */
  result: Schema.NullOr(NewUiComponentResult),
  conversation: WidgetConversationResponseEffectSchema,
});
export type UiComponentResultResponse = typeof UiComponentResultResponse.Type;

/** The UI component message was already approved or denied by a concurrent or earlier result. Nothing ran. */
export class UiComponentAlreadyDecided extends Schema.TaggedError<UiComponentAlreadyDecided>()(
  "UiComponentAlreadyDecided",
  { requestId: Schema.String, status: UiComponentStatus },
  HttpApiSchema.annotations({ status: 409 }),
) {}

/** The UI component message expired before the button was pressed. Nothing ran. */
export class UiComponentExpired extends Schema.TaggedError<UiComponentExpired>()(
  "UiComponentExpired",
  { requestId: Schema.String },
  HttpApiSchema.annotations({ status: 410 }),
) {}

/**
 * The store requires the customer to confirm by magic link before this action. The UI component
 * message stays pending, so the same button works once they have confirmed. `magicLink` is null
 * when the link could not be made.
 */
export class UiComponentNeedsAuthorization extends Schema.TaggedError<UiComponentNeedsAuthorization>()(
  "UiComponentNeedsAuthorization",
  {
    requestId: Schema.String,
    magicLink: Schema.NullOr(
      Schema.Struct({ url: Schema.String, expiresAt: Schema.Date }),
    ),
  },
  HttpApiSchema.annotations({ status: 403 }),
) {}

/** The result does not fit its UI component message, such as an add-to-cart on an order, or a bad sign-in token. */
export class UiComponentResultRejected extends Schema.TaggedError<UiComponentResultRejected>()(
  "UiComponentResultRejected",
  { requestId: Schema.String, message: Schema.String },
  HttpApiSchema.annotations({ status: 400 }),
) {}
